import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { useMemo } from 'react'

import { QUERY_KEYS } from '~constants'
import { getCountriesList, getStatesList, getInterestsList, getCitiesList } from '~services'
import {
  CitiesListResponse,
  CountriesListResponse,
  InterestsListResponse,
  LocationListResponse,
} from '~types/filters'

export const useGetStatesList = (
  countryId?: string,
  options?: UseQueryOptions<LocationListResponse, unknown, LocationListResponse>
) => {
  const query = useQuery<LocationListResponse>(
    [QUERY_KEYS.STATES_LIST, countryId],
    () => getStatesList(countryId),
    {
      enabled: Boolean(countryId),
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      ...options,
    }
  )

  const states = useMemo(
    () =>
      query.data?.states?.map((state) => ({
        label: state.name,
        value: state.id?.toString?.() ?? `${state.id}`,
      })),
    [query.data?.states]
  )

  return {
    states,
    ...query,
  }
}

export const useGetCitiesList = (
  stateId?: string,
  options?: UseQueryOptions<CitiesListResponse, unknown, CitiesListResponse>
) => {
  const query = useQuery<CitiesListResponse>(
    [QUERY_KEYS.CITIES_LIST, stateId],
    () => getCitiesList(stateId),
    {
      enabled: Boolean(stateId),
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      ...options,
    }
  )

  const cities = useMemo(
    () =>
      query.data?.cities?.map((city) => ({
        label: city.name,
        value: city.id?.toString?.() ?? `${city.id}`,
      })),
    [query.data?.cities]
  )

  return {
    cities,
    ...query,
  }
}

export const useGetCountriesList = (
  options?: UseQueryOptions<CountriesListResponse, unknown, CountriesListResponse>
) => {
  const {
    data: countriesList,
    isLoading: isLoadingCountries,
    refetch: refetchCountries,
    status: countriesStatus,
  } = useQuery<CountriesListResponse>([QUERY_KEYS.COUNTRIES_LIST], getCountriesList, {
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    ...options,
  })

  const countries: { label: string; value: string }[] | undefined = useMemo(
    () =>
      countriesList?.countries?.map((country) => ({
        label: country.name,
        value: country.id?.toString?.() ?? `${country.id}`,
      })),
    [countriesList?.countries]
  )

  return {
    countries,
    countriesList,
    countriesStatus,
    isLoadingCountries,
    refetchCountries,
  }
}

export const useGetInterestsList = (
  options?: UseQueryOptions<InterestsListResponse, unknown, InterestsListResponse>
) => {
  const {
    data: interestsList,
    isLoading: isLoadingInterests,
    refetch: refetchInterests,
    status: interestsStatus,
  } = useQuery<InterestsListResponse>([QUERY_KEYS.INTERESTS_LIST], getInterestsList, {
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    ...options,
  })

  const interests: { label: string; value: string }[] | undefined = useMemo(
    () =>
      interestsList?.interests?.map((interest) => {
        return { label: interest.name, value: interest.id }
      }),
    [interestsList?.interests]
  )

  return {
    interests,
    interestsList,
    interestsStatus,
    isLoadingInterests,
    refetchInterests,
  }
}
